import { useEffect, useRef, useState } from 'react';

// How far past the last finished step the bar may creep while the NEXT one is still running: close
// to, but never reaching, the next step's mark - the mark itself is only given by the step being
// really done.
const CREEP_MAX = 0.95;
// Time scale of the creep (ms). The curve is 1 - 1 / (1 + t / TAU), i.e. half of the way after TAU
// and 3/4 after 3 * TAU - fast at first, then ever slower, but never standing still. An
// exponential would have run into its limit after ~7s and left the bar looking frozen exactly
// when the wait is longest.
const TAU_MS = 3000;
const TICK_MS = 100;

/**
 * A progress value (0-100) for "n of m things loaded" that keeps MOVING while the next thing is
 * still loading.
 *
 * The count alone moves in jumps and then stands still for as long as the slowest step takes - the
 * catalog index, several seconds at full size - which reads as "it hangs". Between the real marks
 * the bar therefore creeps forward on a decelerating curve (see TAU_MS). It is an estimate of
 * movement, not of the remaining time, so it never claims a step as done: the value stays below
 * the next mark and is monotonic (a finished step lifts the base to at least where the creep could
 * have got to).
 *
 * @param loaded steps really finished
 * @param total  steps in all
 * @param ready  everything is done - the value is then 100 and the timer stops
 */
export const useLoadProgress = (loaded: number, total: number, ready: boolean): number => {
    const stepStartedAt = useRef(performance.now());
    const [now, setNow] = useState(() => performance.now());

    // A finished step starts the creep over for the next one.
    useEffect(() => {
        stepStartedAt.current = performance.now();
        setNow(stepStartedAt.current);
    }, [loaded]);

    useEffect(() => {
        if (ready) {
            return undefined;
        }
        const timer = setInterval(() => setNow(performance.now()), TICK_MS);
        return () => clearInterval(timer);
    }, [ready]);

    if (ready) {
        return 100;
    }
    const elapsed = Math.max(0, now - stepStartedAt.current);
    const creep = 1 - (1 / (1 + (elapsed / TAU_MS)));
    return (((loaded + (CREEP_MAX * creep)) / total) * 100);
};
